import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { DATE_DATABASE_FORMAT } from 'pared/constants'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { corporateGroupTableConfigs, useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IMetricValue {
  name: string
  unit: string
  value: number
}

interface ILocationGroupNode {
  locationGroupId: number
  startDate: string
  metricData: Record<string, IMetricValue>
  metricSummaryData: Record<string, IMetricValue> | null
}

interface ILocationNode {
  locationId: number
  startDate: string
  metricData: Record<string, IMetricValue>
  metricSummaryData: Record<string, IMetricValue> | null
}

interface IMarketNode {
  locationId: number
  openedAt: string
  market: string
}

type IDataType = {
  trendLocationGroupMetricValues?: {
    nodes: ILocationGroupNode[]
  }
  trendLocationMetricValues?: {
    nodes: ILocationNode[]
  }
  listLocationMarketAndOpenDate?: {
    nodes: IMarketNode[]
  }
}

const query = gql`
  query ListFarwestDayTrendDataV2(
    $iStartDate: Date!
    $iEndDate: Date!
    $iParams: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues: listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: $iParams
    ) @skip(if: $hasGroupBy) {
      nodes {
        locationGroupId
        startDate
        metricData
        metricSummaryData
      }
    }

    trendLocationMetricValues: listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: $iParams
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        startDate
        metricData
        metricSummaryData
      }
    }

    listLocationMarketAndOpenDate(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iParams
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        openedAt
        market
      }
    }
  }
`

export const fwWingstopLastTwentyEightDaysTrendDataConfigs = {
  '<%- JSON(corporateGroup?.tableColumns.slice(0, 1)) %>':
    corporateGroupTableConfigs['<%- JSON(corporateGroup?.tableColumns) %>'],
  '<%- JSON(corporateGroup?.tableColumns.slice(1, 2)) %>':
    corporateGroupTableConfigs['<%- JSON(corporateGroup?.tableColumns) %>'],
  market: 'string',

  // net_sales
  netSalesDay0InPast: 'price',
  netSalesDay1InPast: 'price',
  netSalesDay2InPast: 'price',
  netSalesDay3InPast: 'price',
  netSalesDay4InPast: 'price',
  netSalesDay5InPast: 'price',
  netSalesDay6InPast: 'price',
  netSalesDay7InPast: 'price',
  netSalesDay8InPast: 'price',
  netSalesDay9InPast: 'price',
  netSalesDay10InPast: 'price',
  netSalesDay11InPast: 'price',
  netSalesDay12InPast: 'price',
  netSalesDay13InPast: 'price',
  netSalesDay14InPast: 'price',
  netSalesDay15InPast: 'price',
  netSalesDay16InPast: 'price',
  netSalesDay17InPast: 'price',
  netSalesDay18InPast: 'price',
  netSalesDay19InPast: 'price',
  netSalesDay20InPast: 'price',
  netSalesDay21InPast: 'price',
  netSalesDay22InPast: 'price',
  netSalesDay23InPast: 'price',
  netSalesDay24InPast: 'price',
  netSalesDay25InPast: 'price',
  netSalesDay26InPast: 'price',
  netSalesDay27InPast: 'price',

  // net_sales_percentage_diff_to_last_year
  netSalesPercentageDiffToLastYearDay0InPast: 'percent',
  netSalesPercentageDiffToLastYearDay1InPast: 'percent',
  netSalesPercentageDiffToLastYearDay2InPast: 'percent',
  netSalesPercentageDiffToLastYearDay3InPast: 'percent',
  netSalesPercentageDiffToLastYearDay4InPast: 'percent',
  netSalesPercentageDiffToLastYearDay5InPast: 'percent',
  netSalesPercentageDiffToLastYearDay6InPast: 'percent',
  netSalesPercentageDiffToLastYearDay7InPast: 'percent',
  netSalesPercentageDiffToLastYearDay8InPast: 'percent',
  netSalesPercentageDiffToLastYearDay9InPast: 'percent',
  netSalesPercentageDiffToLastYearDay10InPast: 'percent',
  netSalesPercentageDiffToLastYearDay11InPast: 'percent',
  netSalesPercentageDiffToLastYearDay12InPast: 'percent',
  netSalesPercentageDiffToLastYearDay13InPast: 'percent',
  netSalesPercentageDiffToLastYearDay14InPast: 'percent',
  netSalesPercentageDiffToLastYearDay15InPast: 'percent',
  netSalesPercentageDiffToLastYearDay16InPast: 'percent',
  netSalesPercentageDiffToLastYearDay17InPast: 'percent',
  netSalesPercentageDiffToLastYearDay18InPast: 'percent',
  netSalesPercentageDiffToLastYearDay19InPast: 'percent',
  netSalesPercentageDiffToLastYearDay20InPast: 'percent',
  netSalesPercentageDiffToLastYearDay21InPast: 'percent',
  netSalesPercentageDiffToLastYearDay22InPast: 'percent',
  netSalesPercentageDiffToLastYearDay23InPast: 'percent',
  netSalesPercentageDiffToLastYearDay24InPast: 'percent',
  netSalesPercentageDiffToLastYearDay25InPast: 'percent',
  netSalesPercentageDiffToLastYearDay26InPast: 'percent',
  netSalesPercentageDiffToLastYearDay27InPast: 'percent',

  // check_average
  checkAverageDay0InPast: 'price',
  checkAverageDay1InPast: 'price',
  checkAverageDay2InPast: 'price',
  checkAverageDay3InPast: 'price',
  checkAverageDay4InPast: 'price',
  checkAverageDay5InPast: 'price',
  checkAverageDay6InPast: 'price',
  checkAverageDay7InPast: 'price',
  checkAverageDay8InPast: 'price',
  checkAverageDay9InPast: 'price',
  checkAverageDay10InPast: 'price',
  checkAverageDay11InPast: 'price',
  checkAverageDay12InPast: 'price',
  checkAverageDay13InPast: 'price',
  checkAverageDay14InPast: 'price',
  checkAverageDay15InPast: 'price',
  checkAverageDay16InPast: 'price',
  checkAverageDay17InPast: 'price',
  checkAverageDay18InPast: 'price',
  checkAverageDay19InPast: 'price',
  checkAverageDay20InPast: 'price',
  checkAverageDay21InPast: 'price',
  checkAverageDay22InPast: 'price',
  checkAverageDay23InPast: 'price',
  checkAverageDay24InPast: 'price',
  checkAverageDay25InPast: 'price',
  checkAverageDay26InPast: 'price',
  checkAverageDay27InPast: 'price',

  // check_count
  checkCountDay0InPast: 'number',
  checkCountDay1InPast: 'number',
  checkCountDay2InPast: 'number',
  checkCountDay3InPast: 'number',
  checkCountDay4InPast: 'number',
  checkCountDay5InPast: 'number',
  checkCountDay6InPast: 'number',
  checkCountDay7InPast: 'number',
  checkCountDay8InPast: 'number',
  checkCountDay9InPast: 'number',
  checkCountDay10InPast: 'number',
  checkCountDay11InPast: 'number',
  checkCountDay12InPast: 'number',
  checkCountDay13InPast: 'number',
  checkCountDay14InPast: 'number',
  checkCountDay15InPast: 'number',
  checkCountDay16InPast: 'number',
  checkCountDay17InPast: 'number',
  checkCountDay18InPast: 'number',
  checkCountDay19InPast: 'number',
  checkCountDay20InPast: 'number',
  checkCountDay21InPast: 'number',
  checkCountDay22InPast: 'number',
  checkCountDay23InPast: 'number',
  checkCountDay24InPast: 'number',
  checkCountDay25InPast: 'number',
  checkCountDay26InPast: 'number',
  checkCountDay27InPast: 'number',

  // check_count_percentage_diff_to_last_year
  checkCountPercentageDiffToLastYearDay0InPast: 'percent',
  checkCountPercentageDiffToLastYearDay1InPast: 'percent',
  checkCountPercentageDiffToLastYearDay2InPast: 'percent',
  checkCountPercentageDiffToLastYearDay3InPast: 'percent',
  checkCountPercentageDiffToLastYearDay4InPast: 'percent',
  checkCountPercentageDiffToLastYearDay5InPast: 'percent',
  checkCountPercentageDiffToLastYearDay6InPast: 'percent',
  checkCountPercentageDiffToLastYearDay7InPast: 'percent',
  checkCountPercentageDiffToLastYearDay8InPast: 'percent',
  checkCountPercentageDiffToLastYearDay9InPast: 'percent',
  checkCountPercentageDiffToLastYearDay10InPast: 'percent',
  checkCountPercentageDiffToLastYearDay11InPast: 'percent',
  checkCountPercentageDiffToLastYearDay12InPast: 'percent',
  checkCountPercentageDiffToLastYearDay13InPast: 'percent',
  checkCountPercentageDiffToLastYearDay14InPast: 'percent',
  checkCountPercentageDiffToLastYearDay15InPast: 'percent',
  checkCountPercentageDiffToLastYearDay16InPast: 'percent',
  checkCountPercentageDiffToLastYearDay17InPast: 'percent',
  checkCountPercentageDiffToLastYearDay18InPast: 'percent',
  checkCountPercentageDiffToLastYearDay19InPast: 'percent',
  checkCountPercentageDiffToLastYearDay20InPast: 'percent',
  checkCountPercentageDiffToLastYearDay21InPast: 'percent',
  checkCountPercentageDiffToLastYearDay22InPast: 'percent',
  checkCountPercentageDiffToLastYearDay23InPast: 'percent',
  checkCountPercentageDiffToLastYearDay24InPast: 'percent',
  checkCountPercentageDiffToLastYearDay25InPast: 'percent',
  checkCountPercentageDiffToLastYearDay26InPast: 'percent',
  checkCountPercentageDiffToLastYearDay27InPast: 'percent',
} as const

const KPIS = [
  'net_sales',
  'net_sales_percentage_diff_to_last_year',
  'check_average',
  'check_count',
  'check_count_percentage_diff_to_last_year',
]

const useLastTwentyEightDaysTrendData = () => {
  const { endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { variables } = useVariables()
  const corporateDetails = useMemo(() => {
    if (!variables.corporateGroup) return

    if (
      'locations' in variables.corporateGroup &&
      variables.corporateGroup.locations
    )
      return variables.corporateGroup.locations

    if (
      'locationGroups' in variables.corporateGroup &&
      variables.corporateGroup.locationGroups
    )
      return variables.corporateGroup.locationGroups
  }, [variables])

  const momentEndDate = moment.utc(endDate, DATE_DATABASE_FORMAT, true)
  const startDate = momentEndDate
    .clone()
    .subtract(27, 'days')
    .format(DATE_DATABASE_FORMAT)

  const locationGroupIds = hasGroupBy
    ? groupFilter?.ids
    : groupFilter?.list?.map((g) => g.id)

  const skip = !endDate || !groupFilter || !locationGroupIds?.length
  const iParams = {
    location_group_ids: locationGroupIds,
    intersected_location_group_ids: groupFilter?.intersectedIds,
    group_by: hasGroupBy
      ? ['LOCATION', 'BUSINESS_DAY']
      : ['LOCATION_GROUP', 'BUSINESS_DAY'],
    use_location_history: true,
    metrics: KPIS,
  }

  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iParams,
      hasGroupBy,
    },
    skip,
  })

  return {
    data: useMemo((): IApiDataType => {
      const dateToDayMap: { [dateStr: string]: string } = {}

      for (let di = 0; di < 28; di++) {
        const dateStr = momentEndDate
          .clone()
          .subtract(di, 'days')
          .format(DATE_DATABASE_FORMAT)
        dateToDayMap[dateStr] = `Day${di}InPast`
      }

      const customizedData = hasGroupBy
        ? data?.trendLocationMetricValues?.nodes
        : data?.trendLocationGroupMetricValues?.nodes
      const locationDetails = data?.listLocationMarketAndOpenDate?.nodes

      if (!customizedData) return null

      const idToSourceDataMap: { [id: number]: any } = {}
      const summary: { [key: string]: number } = {}

      customizedData.forEach((nodeData) => {
        const dayStr = dateToDayMap[nodeData.startDate]
        if (!dayStr) return

        const nodeId =
          'locationId' in nodeData
            ? nodeData.locationId
            : nodeData.locationGroupId

        const groupInfo = groupFilter?.list?.find((l) => l.id === nodeId)

        if (!groupInfo?.id || !corporateDetails) {
          return
        }

        let existingData: any = idToSourceDataMap[groupInfo.id]
        if (!existingData) {
          const marketDetail = locationDetails?.find(
            (l) => l.locationId === nodeId,
          )

          existingData = {
            groupInfo,
            market: marketDetail?.market,
            ...Object.values(corporateDetails)?.find(
              (cd) => cd.id === groupInfo.id,
            )?.tableRow,
          }
        }

        for (const key in nodeData.metricData) {
          if (nodeData.metricData.hasOwnProperty(key)) {
            let newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
            newKey = `${newKey}${dayStr}`

            const metricValue = nodeData.metricData[key]
            const isPercentOrDollar =
              metricValue.unit === 'DOLLAR' || metricValue.unit === 'PERCENTAGE'
            const value = isPercentOrDollar
              ? metricValue.value * 100
              : metricValue.value

            existingData[newKey] = value
          }
        }

        if (nodeData.metricSummaryData) {
          for (const key in nodeData.metricSummaryData) {
            if (nodeData.metricSummaryData.hasOwnProperty(key)) {
              let newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
              newKey = `${newKey}${dayStr}`

              const metricValue = nodeData.metricSummaryData[key]
              const isPercentOrDollar =
                metricValue.unit === 'DOLLAR' ||
                metricValue.unit === 'PERCENTAGE'
              summary[newKey] = isPercentOrDollar
                ? metricValue.value * 100
                : metricValue.value
            }
          }
        }

        idToSourceDataMap[groupInfo.id] = existingData
      })

      return [
        {
          parentId: 'root',
          id: 'summary',
          market: 'Total',
          locationGroup: {
            label: 'Total',
          },
          ...summary,
        },
        ...Object.values(idToSourceDataMap)?.map((d) => ({
          parentId: 'summary',
          id: d.groupInfo?.id?.toString(),
          ...d,
        })),
      ]
    }, [groupFilter, hasGroupBy, data, momentEndDate, corporateDetails]),
    loading,
  }
}

export default useLastTwentyEightDaysTrendData
